/**
 * My booking pages (contracts booking.ts, logic/booking; Google Calendar appointment schedules): the Calendar
 * sidebar's list, the editor's save, and the row menu (pause / resume, reset link, delete). Online only; pause is
 * optimistic and rolls back when refused. `editing` is the page the editor shows ("new" for a new one), so the
 * sidebar's Create menu and a row's Edit open the same editor in the Calendar screen.
 */
import type { BookingPage, BookingPageSettings } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { bookingPageProblems, bookingPageRequest } from "../logic/booking.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type BookingPagesViewState = {
  status: "idle" | "loading" | "ready" | "failed";
  /** Mine, oldest first (Google lists them in creation order). */
  pages: BookingPage[];
  /** The page open in the editor: an id, "new", or none. */
  editing?: string;
  saving: boolean;
  error?: MessageDescriptor;
};

export class BookingPagesViewModel extends ViewModel<BookingPagesViewState> {
  constructor(private readonly ws: WorkspaceViewModel) {
    super({ status: "idle", pages: [], saving: false });
  }

  private get api() {
    return this.ws.deps.api;
  }

  /** Loads once (the sidebar calls it when it first shows); `force` reloads. */
  async load(force = false): Promise<void> {
    if (!force && (this.state.status === "loading" || this.state.status === "ready")) return;
    this.update({ status: this.state.status === "ready" ? "ready" : "loading" });
    try {
      const pages = await this.api.listBookingPages(this.ws.wsId);
      if (!this.isDisposed) this.update({ status: "ready", pages: pages.toSorted((a, b) => a.createdAt - b.createdAt) });
    } catch {
      if (!this.isDisposed && this.state.status !== "ready") this.update({ status: "failed" });
    }
  }

  page(id: string | undefined): BookingPage | undefined {
    return id === undefined ? undefined : this.state.pages.find((p) => p.id === id);
  }

  /** Opens the editor on a page, or on a new one. */
  edit(id: string | "new"): void {
    this.setState({ ...this.withoutError(), editing: id });
  }

  closeEditor(): void {
    const { editing: _e, ...rest } = this.withoutError();
    void _e;
    this.setState(rest);
  }

  private withoutError(): BookingPagesViewState {
    const { error: _x, ...rest } = this.state;
    void _x;
    return rest;
  }

  private put(page: BookingPage): void {
    const has = this.state.pages.some((p) => p.id === page.id);
    this.update({ pages: has ? this.state.pages.map((p) => (p.id === page.id ? page : p)) : [...this.state.pages, page] });
  }

  /** Saves the editor's settings (create when `id` is undefined) → the saved page, or undefined when refused. */
  async save(id: string | undefined, settings: BookingPageSettings): Promise<BookingPage | undefined> {
    if (bookingPageProblems(settings).length > 0) return undefined;
    const req = bookingPageRequest(settings);
    this.setState({ ...this.withoutError(), saving: true });
    try {
      const page =
        id === undefined
          ? await this.api.createBookingPage(this.ws.wsId, req)
          : await this.api.updateBookingPage(this.ws.wsId, id, { ...req, description: req.description ?? null, color: req.color ?? null });
      this.put(page);
      this.update({ saving: false });
      return page;
    } catch {
      this.update({ saving: false, error: msg("booking.saveFailed") });
      return undefined;
    }
  }

  /** Pause / resume taking bookings (the link keeps working and says so). */
  async setPaused(id: string, paused: boolean): Promise<void> {
    const old = this.page(id);
    if (old === undefined) return;
    this.put({ ...old, paused });
    try {
      this.put(await this.api.updateBookingPage(this.ws.wsId, id, { paused }));
    } catch {
      this.put(old);
      this.update({ error: msg("booking.saveFailed") });
    }
  }

  /** A new link; the old one stops working. */
  async resetLink(id: string): Promise<BookingPage | undefined> {
    try {
      const page = await this.api.resetBookingPageLink(this.ws.wsId, id);
      this.put(page);
      return page;
    } catch {
      this.update({ error: msg("booking.saveFailed") });
      return undefined;
    }
  }

  /** Deletes it (booked events stay on the calendar). */
  async remove(id: string): Promise<void> {
    const before = this.state.pages;
    this.update({ pages: before.filter((p) => p.id !== id) });
    if (this.state.editing === id) this.closeEditor();
    try {
      await this.api.deleteBookingPage(this.ws.wsId, id);
    } catch {
      this.update({ pages: before, error: msg("booking.deleteFailed") });
    }
  }
}
